import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudents, createStudent, getCourses, enrollStudent, toggleStudentStatus } from '../../../api/adminApi';
import Toast from '../../../components/admin/Toast';
import { toTitleCase } from '../../../utils/format';
import { sanitizeFields } from '../../../utils/sanitize';

const AV_COLORS = ['#E8440A','#F7C91B','#1A7048','#5B6ABF','#D4882A','#8A6FB0','#C74D3A','#2E8B8B'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function StudentList() {
  const [students,    setStudents]    = useState([]);
  const [modal,       setModal]       = useState(null);
  const [toast,       setToast]       = useState(null);
  const [form,        setForm]        = useState({ full_name: '', email: '', password: '' });
  const [courses,     setCourses]     = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('-1');
  const [saving,      setSaving]      = useState(false);
  const [toggling,    setToggling]    = useState(null); // student id being toggled
  const [statusMenu,  setStatusMenu]  = useState(null); // student id whose dropdown is open
  const [deactivateTarget, setDeactivateTarget] = useState(null); // { id, full_name }
  const [deactivateReason, setDeactivateReason] = useState('');
  const [search,           setSearch]           = useState('');
  const navigate = useNavigate();

  const load = () => getStudents().then(d => setStudents(d.students)).catch(console.error);

  async function handleSetStatus(e, student, newStatus) {
    e.stopPropagation();
    setStatusMenu(null);
    if (student.status === newStatus) return;
    if (newStatus === 'Inactive') {
      // Show reason modal before proceeding
      setDeactivateReason('');
      setDeactivateTarget({ id: student.id, full_name: student.full_name });
      return;
    }
    // Activate — no reason needed, go straight through
    await applyStatusChange(student.id, student.full_name, 'Active', '');
  }

  async function applyStatusChange(id, name, newStatus, reason) {
    setToggling(id);
    try {
      const res = await toggleStudentStatus(id, { new_status: newStatus, reason });
      setStudents(prev => prev.map(s => s.id === id ? { ...s, status: res.status } : s));
      setToast({ msg: `${toTitleCase(name)} set to ${res.status}`, type: res.status === 'Active' ? 'success' : 'error' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setToggling(null);
    }
  }

  async function handleDeactivateConfirm() {
    const { id, full_name } = deactivateTarget;
    setDeactivateTarget(null);
    await applyStatusChange(id, full_name, 'Inactive', deactivateReason);
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!statusMenu) return;
    const close = () => setStatusMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [statusMenu]);

  function openAdd() {
    setForm({ full_name: '', email: '', password: '' });
    setSelectedCourse('-1');
    getCourses().then(d => setCourses(d.courses)).catch(console.error);
    setModal('add');
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = sanitizeFields({ ...form }, ['full_name']);
      const res = await createStudent(payload);
      const studentId = res.student.id;
      if (selectedCourse !== '-1') {
        await enrollStudent({ student_id: studentId, course_id: Number(selectedCourse) });
      }
      load();
      setModal(null);
      setToast({ msg: `${form.full_name} added successfully`, type: 'success' });
      if (res.email_sent) {
        setTimeout(() => setToast({ msg: `Welcome email sent to ${form.email}`, type: 'success' }), 2500);
      }
    } catch (err) {
      const m = err.message?.toLowerCase() || '';
      const msg = m.includes('already in use') || m.includes('already exist') || m.includes('409')
        ? `This email is already registered. Please use a different email address.`
        : err.message;
      setToast({ msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const q        = search.toLowerCase();
  const filtered = students.filter(s =>
    s.full_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q)
  );

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Students</div>
          <div className="pt">Student Management</div>
          <div className="ps">{students.length} student{students.length !== 1 ? 's' : ''} on the platform</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="search-box">
            <input
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
          </div>
          <button className="btn btn-dark btn-sm" onClick={openAdd}>+ New Student</button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="body">
        <div className="card" style={{ padding: 0 }}>
          <div className="ct" style={{ padding: '16px 20px' }}>
            {search ? `${filtered.length} of ${students.length} students` : `All Students`}
          </div>
          {students.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">◈</div>
              <div>No students yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Courses</th>
                    <th>Avg Progress</th>
                    <th>Certificates</th>
                    <th>Joined</th>
                    <th>Added</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr
                      key={s.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/students/${s.id}`)}
                    >
                      {/* Student: avatar + name + email */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="av"
                            style={{ background: avColor(s.id), color: 'white', fontSize: 11, flexShrink: 0 }}
                          >
                            {initials(s.full_name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{toTitleCase(s.full_name)}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Courses count */}
                      <td style={{ fontSize: 13 }}>{s.enrollment_count}</td>

                      {/* Avg Progress */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 50, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${s.avg_progress ?? 0}%`, height: '100%', borderRadius: 3, background: 'var(--yellow)' }} />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{s.avg_progress ?? 0}%</span>
                        </div>
                      </td>

                      {/* Certificates */}
                      <td style={{ fontSize: 13 }}>{s.certificate_count ?? 0}</td>

                      {/* Joined */}
                      <td style={{ fontSize: 12.5, color: 'var(--muted)', fontFamily: 'DM Mono, monospace' }}>
                        {fmtDate(s.joined_at)}
                      </td>

                      {/* Added */}
                      <td style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                        {fmtDateTime(s.created_at)}
                      </td>

                      {/* Status — click to open dropdown */}
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span
                            className={`b ${s.status === 'Active' ? 'b-g' : 'b-r'}`}
                            style={{ cursor: toggling === s.id ? 'wait' : 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={e => { e.stopPropagation(); setStatusMenu(statusMenu === s.id ? null : s.id); }}
                          >
                            {toggling === s.id ? '…' : s.status === 'Active' ? 'Active' : 'Inactive'}
                            {toggling !== s.id && <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>}
                          </span>
                          {statusMenu === s.id && (
                            <div style={{
                              position: 'absolute', top: '110%', left: 0, zIndex: 100,
                              background: 'var(--card)', border: '1px solid var(--border)',
                              borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                              minWidth: 130, overflow: 'hidden',
                            }}>
                              {['Active', 'Inactive'].map(opt => (
                                <div
                                  key={opt}
                                  onClick={e => handleSetStatus(e, s, opt)}
                                  style={{
                                    padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    background: s.status === opt ? 'var(--surface)' : 'transparent',
                                    fontWeight: s.status === opt ? 600 : 400,
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                                  onMouseLeave={e => e.currentTarget.style.background = s.status === opt ? 'var(--surface)' : 'transparent'}
                                >
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt === 'Active' ? 'var(--green)' : 'var(--red, #C74D3A)', flexShrink: 0 }} />
                                  {opt}
                                  {s.status === opt && <span style={{ marginLeft: 'auto', fontSize: 11 }}>✓</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Student Modal ── */}
      {modal === 'add' && (
        <div className="modal-overlay">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Add Student
              <button className="btn btn-o btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="fg">
                <label className="fl">Full Name</label>
                <input className="li" placeholder="John Doe" value={form.full_name}
                  onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
              </div>
              <div className="fg">
                <label className="fl">Email</label>
                <input className="li" type="email" placeholder="john@edu" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div className="fg">
                <label className="fl">Password</label>
                <input className="li" type="password" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Leave blank to auto-generate password" />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Enroll in Course <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(optional)</span></label>
                <select
                  className="li"
                  value={selectedCourse}
                  onChange={e => setSelectedCourse(e.target.value)}
                >
                  <option value="-1">None (enroll later)</option>
                  {courses.filter(c => c.status === 'Published').map(c => (
                    <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn btn-o" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={saving}>
                  {saving ? 'Creating…' : 'Add Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Deactivate Reason Modal ── */}
      {deactivateTarget && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Deactivate Student
              <button className="btn btn-o btn-sm" onClick={() => setDeactivateTarget(null)}>✕</button>
            </div>
            <p style={{ fontSize: 13.5, color: 'var(--ink)', marginBottom: 16, lineHeight: 1.6 }}>
              You are about to deactivate <strong>{toTitleCase(deactivateTarget.full_name)}</strong>.
              An email will be sent notifying them. You can include a reason below.
            </p>
            <div className="fg">
              <label className="fl">Reason <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
              <textarea
                className="li"
                rows={3}
                placeholder="e.g. Violated platform policy, account under review…"
                value={deactivateReason}
                onChange={e => setDeactivateReason(e.target.value)}
                style={{ resize: 'vertical', minHeight: 80 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button className="btn btn-o" onClick={() => setDeactivateTarget(null)}>Cancel</button>
              <button
                className="btn"
                style={{ background: '#C74D3A', color: 'white' }}
                onClick={handleDeactivateConfirm}
              >
                Deactivate &amp; Notify
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}
    </>
  );
}
